import { useCallback, useState } from "react";
import { runClinicalAnalysis, ApiError } from "@/lib/api";
import { useDictationStore } from "@/stores/dictation";
import type { ClinicalAnalysisResult, GroundedClinicalFinding } from "@/types/api";
import { Footer } from "@/components/Footer";
import {
  MarketingAppBar,
  type MarketingAppBarItem,
} from "@/components/marketing/MarketingAppBar";

const NAV_ITEMS: MarketingAppBarItem[] = [
  { label: "Richtlijnen", href: "/richtlijnen" },
  { label: "Privacy", href: "/privacy" },
  { label: "FAQ", href: "/#faq" },
];

const FRAME_CLASS =
  "rounded-[28px] sm:rounded-[34px] border border-[rgba(10,52,96,0.08)] bg-white/85 shadow-[0_18px_48px_rgba(10,52,96,0.10)] backdrop-blur-sm";

const CLASS_CONFIG: Record<string, { label: string; bg: string; text: string; border: string }> = {
  I: { label: "Klasse I", bg: "bg-emerald-100", text: "text-emerald-800", border: "border-emerald-200" },
  IIa: { label: "Klasse IIa", bg: "bg-blue-100", text: "text-blue-800", border: "border-blue-200" },
  IIb: { label: "Klasse IIb", bg: "bg-amber-100", text: "text-amber-800", border: "border-amber-200" },
  III: { label: "Klasse III", bg: "bg-rose-100", text: "text-rose-800", border: "border-rose-200" },
};

const SEVERITY_LABELS: Record<string, { label: string; badge: string }> = {
  critical: { label: "KRITIEK", badge: "bg-red-100 text-red-700" },
  high: { label: "HOOG", badge: "bg-amber-100 text-amber-700" },
  medium: { label: "MATIG", badge: "bg-blue-100 text-blue-700" },
  low: { label: "LAAG", badge: "bg-slate-100 text-slate-600" },
};

const CATEGORY_LABELS: Record<string, string> = {
  veiligheid: "🛡️ Veiligheid",
  diagnostiek: "🔬 Diagnostiek",
  medicatie: "💊 Medicatie",
  interventie: "🔧 Interventie",
  monitoring: "📋 Monitoring",
  risicobeoordeling: "📊 Risicobeoordeling",
  overig: "📌 Overig",
};

const CATEGORY_ORDER = [
  "veiligheid",
  "medicatie",
  "diagnostiek",
  "interventie",
  "monitoring",
  "risicobeoordeling",
  "overig",
];

function FindingCard({ finding }: { finding: GroundedClinicalFinding }) {
  const [expanded, setExpanded] = useState(false);
  const sev = SEVERITY_LABELS[finding.severity] ?? SEVERITY_LABELS.low!;

  const borderColor =
    finding.severity === "critical" || finding.severity === "high"
      ? "border-amber-200 bg-amber-50/30"
      : finding.severity === "medium"
        ? "border-blue-200 bg-blue-50/20"
        : "border-slate-200 bg-slate-50/30";

  return (
    <div
      className={`rounded-2xl border shadow-[0_8px_22px_rgba(10,52,96,0.06)] overflow-hidden transition-all ${borderColor}`}
    >
      <button
        type="button"
        onClick={() => setExpanded(!expanded)}
        className="w-full px-4 py-3 flex items-center gap-2 text-left"
      >
        <span className="font-semibold text-sm text-[var(--cs-navy)]">{finding.topic ?? "Observatie"}</span>
        {finding.action ? (
          <span className="text-[10px] font-medium text-blue-700 bg-blue-50 px-1.5 py-0.5 rounded border border-blue-200/60">
            {finding.action}
          </span>
        ) : null}
        <span className={`ml-auto px-1.5 py-0.5 text-[9px] font-bold uppercase rounded ${sev.badge}`}>
          {sev.label}
        </span>
        <svg
          className={`w-3.5 h-3.5 text-[var(--cs-ink-subtle)] transition-transform ${expanded ? "rotate-180" : ""}`}
          viewBox="0 0 20 20"
          fill="currentColor"
          aria-hidden="true"
        >
          <path
            fillRule="evenodd"
            d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
            clipRule="evenodd"
          />
        </svg>
      </button>

      <p className="px-4 pb-3 -mt-1 text-sm text-[rgba(10,52,96,0.78)] leading-relaxed">{finding.text}</p>

      {finding.uptodate_url ? (
        <div className="px-4 pb-3">
          <a
            href={finding.uptodate_url}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1 px-2 py-0.5 text-[10px] font-medium bg-orange-50 text-orange-700 rounded-full border border-orange-200 hover:bg-orange-100"
          >
            UpToDate: {finding.uptodate_label ?? finding.uptodate_topic}
          </a>
        </div>
      ) : null}

      {expanded && finding.linked_recommendations.length > 0 ? (
        <div className="border-t border-[rgba(10,52,96,0.10)] bg-white/70 px-4 py-2 space-y-2">
          <span className="text-[9px] font-semibold text-[var(--cs-ink-muted)] uppercase tracking-wider">
            Onderliggende aanbevelingen
          </span>
          {finding.linked_recommendations.map((ref, i) => {
            const cfg = CLASS_CONFIG[ref.class_of_recommendation ?? ""] ?? CLASS_CONFIG.I!;
            const href = ref.guideline_section_url
              ?? (ref.guideline_doi ? `https://doi.org/${ref.guideline_doi}` : null);
            return (
              <div key={`${ref.guideline_code}-${i}`} className={`rounded-lg border ${cfg.border} ${cfg.bg}/30 p-2.5 text-[11px]`}>
                <div className="flex items-center gap-1.5 mb-1 flex-wrap">
                  <span className={`px-1 py-0.5 rounded text-[9px] font-bold ${cfg.bg} ${cfg.text}`}>
                    {cfg.label}
                  </span>
                  {ref.level_of_evidence ? (
                    <span className="px-1 py-0.5 rounded text-[9px] font-medium bg-slate-100 text-slate-600">
                      Niveau {ref.level_of_evidence}
                    </span>
                  ) : null}
                  <span className="text-[9px] text-slate-400 ml-auto">
                    {ref.guideline_title}
                    {ref.section ? ` · ${ref.section}` : ""}
                    {ref.page ? ` · p.${ref.page}` : ""}
                  </span>
                </div>
                <p className="text-[rgba(10,52,96,0.78)] leading-relaxed">{ref.recommendation_text_preview}</p>
                {href ? (
                  <a
                    href={href}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="mt-1.5 inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-md text-[9px] font-medium bg-blue-50 text-blue-600 border border-blue-100 hover:bg-blue-100 no-underline"
                  >
                    ESC ↗
                  </a>
                ) : null}
              </div>
            );
          })}
        </div>
      ) : null}
    </div>
  );
}

export function RichtlijnenPage() {
  const [inputText, setInputText] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const analysisResult = useDictationStore((s) => s.analysisResult);
  const setAnalysisResult = useDictationStore((s) => s.setAnalysisResult);
  const showToast = useDictationStore((s) => s.showToast);

  const runAnalysis = useCallback(async () => {
    if (!inputText.trim()) return;
    setLoading(true);
    setError(null);
    try {
      const result = await runClinicalAnalysis(inputText);
      setAnalysisResult(result);
      showToast(`${result.total_findings} observaties, ${result.total_recommendations} aanbevelingen`, "success");
    } catch (e) {
      const msg = e instanceof ApiError ? e.message : "Analyse mislukt";
      setError(msg);
      showToast(msg, "error");
    } finally {
      setLoading(false);
    }
  }, [inputText, setAnalysisResult, showToast]);

  const groupedByCategory = groupFindings(analysisResult);

  return (
    <div className="flex min-h-screen flex-col bg-[linear-gradient(180deg,#fcfbf7_0%,#ffffff_22%,#f9fbfc_100%)] text-[var(--cs-navy)]">
      <MarketingAppBar
        items={NAV_ITEMS}
        activeItem="/richtlijnen"
        mobileMenuOpen={mobileMenuOpen}
        navScrolled={false}
        onToggleMenu={() => setMobileMenuOpen((current) => !current)}
      />
      <main className="flex-1 px-6 pb-16 pt-28 sm:pt-32">
        <header className="mx-auto max-w-[1120px] text-center">
          <h1 className="text-[34px] font-semibold tracking-[-0.03em] text-[var(--cs-navy)] sm:text-[44px]">
            Richtlijn-Analyse
          </h1>
          <p className="mx-auto mt-3 max-w-[640px] text-[16px] leading-7 text-[var(--cs-navy)]/75 sm:text-[17px]">
            Plak een consultverslag en controleer het tegen de ESC-richtlijnen.
          </p>
        </header>

        <section className={`mx-auto mt-10 w-full max-w-[820px] p-6 sm:p-8 ${FRAME_CLASS}`}>
          <label className="text-sm font-medium text-[var(--cs-navy)]" htmlFor="consultatietekst">
            Consultatietekst
          </label>
          <textarea
            id="consultatietekst"
            value={inputText}
            onChange={(e) => setInputText(e.target.value)}
            rows={10}
            className="mt-2 w-full rounded-xl border border-[rgba(10,52,96,0.18)] bg-white px-4 py-3 text-sm leading-relaxed text-[var(--cs-navy)] placeholder:text-[var(--cs-ink-subtle)] focus:outline-none focus:ring-2 focus:ring-[var(--cs-rose)]"
            placeholder="man 84 jaar&#10;diabetes, HTN&#10;de novo VKF&#10;dyspneu&#10;start bisoprolol"
          />
          <div className="mt-3 flex items-center justify-between">
            <span className="text-xs text-[var(--cs-ink-subtle)]">{inputText.length} tekens</span>
            <button
              type="button"
              onClick={runAnalysis}
              disabled={loading || !inputText.trim()}
              className="rounded-full bg-[var(--cs-navy)] px-6 py-3 text-[15px] font-medium text-white transition-all duration-200 hover:bg-[var(--cs-navy-strong)] disabled:opacity-50"
            >
              {loading ? "Analyseren…" : "Analyseer"}
            </button>
          </div>
        </section>

        {error ? (
          <div className="mx-auto mt-4 w-full max-w-[820px] rounded-2xl border border-red-200 bg-red-50 p-3 text-sm text-red-700">
            {error}
          </div>
        ) : null}

        {analysisResult ? (
          <div className="mx-auto mt-12 w-full max-w-[1120px] animate-fade-in-up">
            <div className="mb-4 flex items-center gap-3 text-sm text-[var(--cs-ink-muted)]">
              <span className="font-semibold text-[var(--cs-navy)]">{analysisResult.total_findings}</span>{" "}
              bevindingen
              <span className="text-[rgba(10,52,96,0.18)]">|</span>
              <span>
                {analysisResult.total_recommendations} aanbevelingen uit {analysisResult.groups.length}{" "}
                richtlijnen
              </span>
              <span className="text-[rgba(10,52,96,0.18)]">|</span>
              <span>{(analysisResult.processing_time_ms / 1000).toFixed(1)}s</span>
            </div>

            {analysisResult.total_findings === 0 ? (
              <div className="rounded-2xl border border-emerald-200 bg-emerald-50 p-6 text-center">
                <span className="text-2xl">✅</span>
                <p className="mt-2 text-sm font-medium text-emerald-700">
                  Geen afwijkingen gevonden ten opzichte van de ESC-richtlijnen.
                </p>
              </div>
            ) : (
              <div className="space-y-6">
                {groupedByCategory.map((group) => (
                  <section key={group.category}>
                    <h2 className="mb-3 text-[12px] font-semibold uppercase tracking-[0.18em] text-[#89A4BD]">
                      {CATEGORY_LABELS[group.category] ?? group.category}
                    </h2>
                    <div className="space-y-2">
                      {group.findings.map((f, i) => (
                        <FindingCard key={`${group.category}-${i}`} finding={f} />
                      ))}
                    </div>
                  </section>
                ))}
              </div>
            )}
          </div>
        ) : null}
      </main>
      <Footer />
    </div>
  );
}

function groupFindings(
  result: ClinicalAnalysisResult | null,
): Array<{ category: string; findings: GroundedClinicalFinding[] }> {
  if (!result) return [];
  const byCategory = new Map<string, GroundedClinicalFinding[]>();
  for (const f of result.clinical_findings) {
    const cat = f.category ?? "overig";
    const list = byCategory.get(cat) ?? [];
    list.push(f);
    byCategory.set(cat, list);
  }
  return CATEGORY_ORDER.filter((c) => byCategory.has(c)).map((category) => ({
    category,
    findings: byCategory.get(category) ?? [],
  }));
}
